import React from 'react';
import { motion } from 'framer-motion';
import { Instagram } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const WEBSITE_URL = 'verahilariofotografia.com';
const INSTAGRAM_HANDLE = '@verahilariofotografia';
const LOGO_SRC = '/images/logo.svg';

function formatDate(iso, language) {
  const d = new Date(iso);
  if (language === 'pt') {
    const months = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
      'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];
    return `${d.getDate()} de ${months[d.getMonth()]} de ${d.getFullYear()}`;
  }
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
}

function CornerFlourish({ position }) {
  const styles = {
    'tl': { top: 8, left: 8 },
    'tr': { top: 8, right: 8, transform: 'scaleX(-1)' },
    'bl': { bottom: 8, left: 8, transform: 'scaleY(-1)' },
    'br': { bottom: 8, right: 8, transform: 'scale(-1, -1)' },
  };
  return (
    <svg
      width="36"
      height="36"
      viewBox="0 0 36 36"
      style={{ position: 'absolute', ...styles[position] }}
      aria-hidden="true"
    >
      <g stroke="#c9a86a" strokeWidth="0.6" fill="none">
        <line x1="0" y1="0" x2="36" y2="0" />
        <line x1="0" y1="0" x2="0" y2="36" />
        <line x1="6" y1="6" x2="30" y2="6" />
        <line x1="6" y1="6" x2="6" y2="30" />
        <circle cx="18" cy="18" r="1.4" />
      </g>
    </svg>
  );
}

function Ornament() {
  return (
    <svg
      width="180"
      height="10"
      viewBox="0 0 180 10"
      aria-hidden="true"
      style={{ display: 'block', margin: '0 auto' }}
    >
      <line x1="0" y1="5" x2="78" y2="5" stroke="#c9a86a" strokeWidth="0.8" />
      <line x1="102" y1="5" x2="180" y2="5" stroke="#c9a86a" strokeWidth="0.8" />
      <polygon points="90,1 95,5 90,9 85,5" fill="#c9a86a" />
    </svg>
  );
}

const cardOuterStyle = {
  position: 'relative',
  background: '#faf6ee',
  padding: '24px 56px 20px',
  border: '1px solid #c9a86a',
  backgroundImage:
    'radial-gradient(ellipse at top left, rgba(201,168,106,0.08), transparent 60%), radial-gradient(ellipse at bottom right, rgba(168,130,90,0.06), transparent 60%)',
};

const cardInnerBorderStyle = {
  position: 'absolute',
  inset: 8,
  border: '1px solid rgba(201,168,106,0.45)',
  pointerEvents: 'none',
};

export function VoucherDisplay({ voucher }) {
  const { t, language } = useLanguage();
  if (!voucher) return null;

  const termsTitle = t.vouchers?.terms?.title || (language === 'pt' ? 'Termos e Condições' : 'Terms & Conditions');
  const termsItems = t.vouchers?.terms?.items || [];

  return (
    <div className="mx-auto" style={{ maxWidth: 960 }}>
      {/* FRONT */}
      <motion.div
        initial={{ opacity: 0, y: 20, rotate: -1 }}
        animate={{ opacity: 1, y: 0, rotate: 0 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="relative shadow-2xl"
        style={cardOuterStyle}
      >
        <div style={cardInnerBorderStyle} />
        <CornerFlourish position="tl" />
        <CornerFlourish position="tr" />
        <CornerFlourish position="bl" />
        <CornerFlourish position="br" />

        <div className="relative text-center">
          <img
            src={LOGO_SRC}
            alt="Vera Hilário Fotografia"
            style={{ height: 56, margin: '0 auto', display: 'block' }}
          />
          <div className="text-[10px] tracking-[2px] text-[#7a6a55] uppercase mt-1">
            {t.vouchers.voucher.atalaia} · {WEBSITE_URL}
          </div>

          <div
            className="font-heading text-[#c9a86a] mt-4 mb-1"
            style={{ fontSize: 40, letterSpacing: 8, fontWeight: 700 }}
          >
            {t.vouchers.voucher.label}
          </div>
          <Ornament />
        </div>

        <div
          className="grid grid-cols-2 gap-8 mt-6 relative items-start"
          style={{
            backgroundImage:
              'linear-gradient(to bottom, transparent 10%, rgba(201,168,106,0.4) 10%, rgba(201,168,106,0.4) 90%, transparent 90%)',
            backgroundSize: '1px 100%',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        >
          <div className="text-center">
            <div className="text-[10px] tracking-[2px] text-[#7a6a55] uppercase mb-2">
              {t.vouchers.voucher.forName}
            </div>
            <div
              className="font-heading text-[#3a2e20] italic"
              style={{ fontSize: 30, lineHeight: 1.1 }}
            >
              {voucher.recipient_name}
            </div>
          </div>

          <div className="text-center">
            <div className="text-[10px] tracking-[2px] text-[#7a6a55] uppercase mb-2">
              {t.vouchers.voucher.sessionLabel}
            </div>
            <div className="text-[#3a2e20] font-semibold text-base mb-1">
              {voucher.service_name}
            </div>
            <div className="text-[#7a6a55] text-sm">
              {voucher.service_details}
            </div>
          </div>
        </div>

        {voucher.personal_message ? (
          <div className="relative text-center text-[#7a6a55] italic text-sm max-w-xl mx-auto mt-5 leading-relaxed">
            “{voucher.personal_message}”
          </div>
        ) : null}

        <div
          className="grid grid-cols-3 mt-5 pt-4 relative"
          style={{ borderTop: '1px solid rgba(201,168,106,0.45)' }}
        >
          <div className="text-center">
            <div className="text-[10px] tracking-[2px] text-[#7a6a55] uppercase mb-1">
              {t.vouchers.voucher.validUntil}
            </div>
            <div className="text-[#3a2e20] font-semibold text-sm">
              {formatDate(voucher.expiry_date, language)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-[10px] tracking-[2px] text-[#7a6a55] uppercase mb-1">
              {t.vouchers.voucher.code}
            </div>
            <div
              className="text-[#a8825a] font-bold"
              style={{ fontFamily: 'Courier New, monospace', fontSize: 14, letterSpacing: 1 }}
            >
              {voucher.code}
            </div>
          </div>
          <div className="text-center">
            <div
              className="text-[#3a2e20] italic"
              style={{ fontSize: 16 }}
            >
              Vera Hilário
            </div>
            <div className="text-[9px] tracking-[2px] text-[#7a6a55] uppercase mt-0.5">
              {t.vouchers.voucher.issuedBy}
            </div>
          </div>
        </div>

        <div className="text-center mt-3 text-[10px] tracking-[2px] text-[#7a6a55] uppercase relative">
          {WEBSITE_URL} · {INSTAGRAM_HANDLE}
        </div>
      </motion.div>

      {/* BACK */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.15, ease: 'easeOut' }}
        className="relative shadow-2xl mt-8"
        style={cardOuterStyle}
      >
        <div style={cardInnerBorderStyle} />
        <CornerFlourish position="tl" />
        <CornerFlourish position="tr" />
        <CornerFlourish position="bl" />
        <CornerFlourish position="br" />

        {/* Watermark */}
        <img
          src={LOGO_SRC}
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '70%',
            opacity: 0.06,
            pointerEvents: 'none',
          }}
        />

        <div className="relative">
          <div className="text-center">
            <div className="text-[10px] tracking-[3px] text-[#7a6a55] uppercase mb-2">
              {t.vouchers.voucher.issuedBy}
            </div>
            <div style={{ width: 64, height: 1, background: '#c9a86a', margin: '0 auto 18px' }} />
            <h3
              className="font-heading text-[#3a2e20] mb-2"
              style={{ fontSize: 26, fontWeight: 700 }}
            >
              {termsTitle}
            </h3>
            <Ornament />
          </div>

          <ol className="mt-8 space-y-4 max-w-xl mx-auto">
            {termsItems.map((item, i) => (
              <li key={i} className="flex gap-4 text-[#3a2e20] text-sm leading-relaxed">
                <span
                  className="text-[#c9a86a] font-bold shrink-0"
                  style={{ fontSize: 14, minWidth: 22 }}
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <span>{item}</span>
              </li>
            ))}
          </ol>

          <div className="mt-10 text-center">
            <div className="text-[#7a6a55] italic text-xs leading-relaxed mb-3">
              {language === 'pt'
                ? 'Apresente este voucher na sua sessão. Marcações sujeitas a disponibilidade.'
                : 'Please present this voucher at your session. Bookings subject to availability.'}
            </div>
            <div className="flex items-center justify-center gap-3 text-[#3a2e20] text-xs">
              <span>{WEBSITE_URL}</span>
              <span className="text-[#c9a86a]">·</span>
              <span className="inline-flex items-center gap-1">
                <Instagram className="w-3 h-3" />
                {INSTAGRAM_HANDLE}
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
